import { useMemo, useState, type FormEvent } from 'react';
import type { DeckSection } from '../../shared/deck.ts';
import type { NewSection } from '../editor/useDeckEditor.ts';
import AccentSwatches from './AccentSwatches.tsx';

/** Derive a manifest-safe section key from a display name. */
function keyFromName(name: string, existingKeys: Set<string>): string {
  let base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!/^[a-z]/.test(base)) base = 's-' + base;
  if (!base) base = 'section';
  let key = base;
  let i = 2;
  while (existingKeys.has(key) || key === 'intro' || key === 'closing') {
    key = `${base}-${i}`;
    i += 1;
  }
  return key;
}

/**
 * Inline add-section form at the foot of the filmstrip: name + colour +
 * where it slots in. The key is derived from the name — non-coders never
 * see it. Creating a section also writes its divider slide (server-side).
 */
export default function AddSectionForm({
  sections,
  onCreate,
  onCancel,
  busy,
}: {
  sections?: DeckSection[];
  onCreate: (section: NewSection) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const contentSections = useMemo(() => (sections || []).filter((s) => s.key !== 'closing'), [sections]);
  const [name, setName] = useState('');
  // Default to the deck's base accent (the intro section's colour) so a
  // blank-start deck's chosen colour carries into new sections.
  const [color, setColor] = useState(() => (sections || []).find((s) => s.key === 'intro')?.color || 'ORANGE');
  const [insertAfter, setInsertAfter] = useState(contentSections[contentSections.length - 1]?.key || 'intro');

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    const existingKeys = new Set((sections || []).map((s) => s.key));
    onCreate({ key: keyFromName(trimmed, existingKeys), name: trimmed, color, insertAfter });
  }

  return (
    <form className="strip-addsection" onSubmit={submit}>
      <input
        className="strip-addsection__name"
        placeholder="Section name…"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        maxLength={80}
        autoFocus
      />
      <AccentSwatches value={color} onPick={setColor} disabled={busy} />
      <label className="strip-addsection__after muted small">
        After
        <select value={insertAfter} onChange={(e) => setInsertAfter(e.target.value)}>
          {contentSections.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name || s.key}
            </option>
          ))}
        </select>
      </label>
      <div className="strip-addsection__actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !name.trim()}>
          Add section
        </button>
      </div>
    </form>
  );
}
